import React, { ChangeEvent, ReactElement, useEffect, useMemo } from 'react';
import { Box, Grid2, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import {
    removeBareGoodsEntity,
    setBareGoodsGoodsList,
    updateBareGoodsArticle,
    updateBareGoodsAvailableQuantity,
    updateBareGoodsOrderedQuantity,
} from '../../../redux/actions/orderActions';
import { BareGoodsEntity } from '../../../redux/reducers/goodsToOrderFormReducer';
import { getBareGoodsForSite, isOriginSiteKnown } from '../../../redux/selectors/ordersSelector';
import { RootState } from '../../../redux/reducers';
import { GoodsElementFormContainerBox } from '../../../common/ResponsiveStyle';
import DeleteIcon from '@mui/icons-material/Delete';
import { CompleteOrderType, DimensionTypeDimensionSR, DimensionTypeSR } from '../../../common/Types';
import ArticleAutocomplete from '../../../common/components/autocomplete/ArticleAutocomplete';

type OrderFormGoodsElementProps = {
    orderedBareGoods: BareGoodsEntity;
    completeOrderType?: CompleteOrderType;
    createNewBareGoods(): void;
};

// get all goods from site
// reduce() quantity of goods
// give those articles as choices with total quantity
export default function OrderFormGoodsElement(props: OrderFormGoodsElementProps): ReactElement {
    const { orderedBareGoods, completeOrderType, createNewBareGoods } = props;

    const articles = useAppSelector((state: RootState) => state.articles.articles);
    const demandOrders = useAppSelector((state: RootState) => state.demandOrders.demandOrders);
    const returnOrders = useAppSelector((state: RootState) => state.returnOrders.returnOrders);
    const order = useAppSelector((state: RootState) => state.orderForm.order);
    const returnOrder = useAppSelector((state: RootState) => state.returnOrderForm.returnOrder);
    const orderedGoods = useAppSelector((state: RootState) => state.goodsToOrderForm.bareGoodsEntities);

    const approvedOrderGoodsItems = useAppSelector((state: RootState) => state.orderGoodsItems.approvedOrderGoodsItems);

    const isThereOriginSite = useAppSelector(isOriginSiteKnown);

    const dispatch = useAppDispatch();

    const bareGoodsForSite = useAppSelector(
        getBareGoodsForSite(
            completeOrderType,
            approvedOrderGoodsItems?.filter(
                (ogi) =>
                    !!ogi.order &&
                    ((completeOrderType === CompleteOrderType.Transfer &&
                        ogi.order.originSiteId === order?.originSiteId) ||
                        (completeOrderType === CompleteOrderType.GeneralReturn &&
                            ogi.order.originSiteId === returnOrder?.originSiteId)) &&
                    ((completeOrderType === CompleteOrderType.Transfer && ogi.orderId !== order?.id) ||
                        (completeOrderType === CompleteOrderType.GeneralReturn &&
                            ogi.returnOrderId !== returnOrder?.id)),
            ),
        ),
    );

    const filteredArticles = useMemo(() => {
        if (!articles) {
            return [];
        }

        if (!order) {
            return articles.filter((a) => !orderedGoods?.find((og) => og.article?.id === a.id));
        }

        if (!!order.demandOrderId && !!demandOrders) {
            const demandOrderItems = demandOrders.find((dord) => dord.id === order?.demandOrderId)?.orderGoodsItems;

            return articles
                .filter((a) => !!demandOrderItems?.find((doi) => doi.articleId === a.id))
                .filter((a) => !orderedGoods?.find((og) => og.article?.id === a.id));
        }

        if (!!order.returnOrderId && !!returnOrders) {
            const returnOrderItems = returnOrders.find((rord) => rord.id === order?.returnOrderId)?.orderGoodsItems;

            return articles
                .filter((a) => !!returnOrderItems?.find((roi) => roi.articleId === a.id))
                .filter((a) => !orderedGoods?.find((og) => og.article?.id === a.id));
        }

        return articles.filter((a) => !orderedGoods?.find((og) => og.article?.id === a.id));
    }, [articles, order, demandOrders, returnOrders, orderedGoods]);

    const filteredBareGoodsForSite = useMemo(() => {
        if (!bareGoodsForSite) {
            return [];
        }

        if (!order) {
            return bareGoodsForSite.filter((a) => !orderedGoods?.find((og) => og.article?.id === a.article?.id));
        }

        if (!!order.demandOrderId && !!demandOrders) {
            const demandOrderItems = demandOrders.find((dord) => dord.id === order?.demandOrderId)?.orderGoodsItems;

            return bareGoodsForSite
                .filter((bgfs) => !!demandOrderItems?.find((doi) => doi.articleId === bgfs.article?.id))
                .filter((a) => !orderedGoods?.find((og) => og.article?.id === a.article?.id));
        }

        if (!!order.returnOrderId && !!returnOrders) {
            const returnOrderItems = returnOrders.find((rord) => rord.id === order?.returnOrderId)?.orderGoodsItems;

            return bareGoodsForSite
                .filter((bgfs) => !!returnOrderItems?.find((roi) => roi.articleId === bgfs.article?.id))
                .filter((a) => !orderedGoods?.find((og) => og.article?.id === a.article?.id));
        }

        return bareGoodsForSite.filter((a) => !orderedGoods?.find((og) => og.article?.id === a.article?.id));
    }, [bareGoodsForSite, order, demandOrders, returnOrders]);

    const handleRemoveBareGood = () => {
        if (!!orderedBareGoods.index) {
            dispatch(removeBareGoodsEntity(orderedBareGoods.index));
        }
    };

    const handleBareGoodsOrderedQuantityChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const type = e.target.type;

        if (!isNaN(+value) && type === 'number') {
            dispatch(updateBareGoodsOrderedQuantity({ index: orderedBareGoods.index, orderedQuantity: +value }));
        }
    };

    const handleEnterPressed = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const code = e.code;

        if (code === 'Enter' || code === 'NumpadEnter') {
            if (orderedBareGoods.index === Math.max(...(orderedGoods?.map((og) => og.index) ?? []))) {
                createNewBareGoods();
            }
        }
    };

    const handleSelectArticle = (articleId?: number) => {
        const article = articles?.find((a) => a.id === articleId);
        const bareGoods = bareGoodsForSite?.find((bg) => bg.article?.id === articleId);

        dispatch(
            updateBareGoodsAvailableQuantity({
                index: orderedBareGoods.index,
                availableQuantity: bareGoods?.availableQuantity ?? Number.MAX_SAFE_INTEGER,
            }),
        );
        dispatch(updateBareGoodsArticle({ index: orderedBareGoods.index, article }));
        dispatch(setBareGoodsGoodsList({ index: orderedBareGoods.index, bareGoods: bareGoods?.goods }));
    };

    return (
        !!orderedBareGoods && (
            <GoodsElementFormContainerBox>
                <Grid2 container spacing={2} sx={{ justifyContent: 'start', width: '100%' }}>
                    <Grid2
                        size={{
                            xs: 12,
                            sm: 8,
                            md: completeOrderType === CompleteOrderType.Demand ? 9 : 8,
                            lg: completeOrderType === CompleteOrderType.Demand ? 10 : 8,
                        }}
                    >
                        <Tooltip title={!!orderedBareGoods.article ? orderedBareGoods.article.title : ''}>
                            <div>
                                <ArticleAutocomplete
                                    articles={
                                        isThereOriginSite
                                            ? (filteredBareGoodsForSite.map((b) => b.article ?? []).flat() ?? [])
                                            : filteredArticles
                                    }
                                    isRequired
                                    selectArticle={handleSelectArticle}
                                    selectedArticleId={orderedBareGoods.article?.id}
                                />
                            </div>
                        </Tooltip>
                    </Grid2>
                    <Grid2
                        size={{
                            xs: 12,
                            sm: 4,
                            md: completeOrderType === CompleteOrderType.Demand ? 3 : 4,
                            lg: completeOrderType === CompleteOrderType.Demand ? 2 : 4,
                        }}
                    >
                        <TextField
                            error={
                                !!orderedBareGoods.orderedQuantity && orderedBareGoods.orderedQuantity > 0
                                    ? false
                                    : true
                            }
                            required
                            fullWidth
                            name="orderedQuantity"
                            label={
                                orderedBareGoods.article?.dimensionType
                                    ? DimensionTypeSR[orderedBareGoods.article.dimensionType]
                                    : 'Količina'
                            }
                            slotProps={{
                                htmlInput: { inputMode: 'decimal' },
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {orderedBareGoods.availableQuantity !== Number.MAX_SAFE_INTEGER
                                                ? orderedBareGoods.availableQuantity
                                                : '/'}{' '}
                                            (
                                            {orderedBareGoods.article?.dimensionType
                                                ? DimensionTypeDimensionSR[orderedBareGoods.article.dimensionType]
                                                : 'kom.'}
                                            )
                                        </InputAdornment>
                                    ),
                                },
                            }}
                            type="number"
                            value={!!orderedBareGoods.orderedQuantity ? orderedBareGoods.orderedQuantity : ''}
                            variant="outlined"
                            onChange={handleBareGoodsOrderedQuantityChange}
                            onKeyDown={handleEnterPressed}
                        />
                    </Grid2>
                </Grid2>
                <Box style={{ flexGrow: 1 }} />
                <Tooltip title="Ukloni artikl" placement="left">
                    <IconButton sx={{ height: 40 }} onClick={handleRemoveBareGood}>
                        <DeleteIcon color="error" />
                    </IconButton>
                </Tooltip>
            </GoodsElementFormContainerBox>
        )
    );
}
